import { useEffect, useRef, useState } from 'react'
import {
  MFA_SETTINGS_DISABLE,
  MFA_SETTINGS_ENABLE,
  MFA_SETTINGS_LOCAL_LOGIN_DISABLED,
  MFA_SETTINGS_TITLE,
  RECOVERY_OTP_CONFIG_ERROR,
  REVOKE_MFA_MECHANISM_ENFORCED_MESSAGE,
} from '../../constants/ui.labels'
import SettingItem from '../Settings/SettingItem'
import RecoveryCodesOption from './RecoveryCodesOption'
import MechanismsList from './MechanismsList'
import mapError from '../../utils/mapError/mfa'
import { Properties, RECOVERY_OTP } from '../../constants/mfa'
import RecoveryCodesRenewConfirm from './RecoveryCodesRenewConfirm'
import ToastError from '../ToastError'
import WarningMessage from '../WarningMessage'
import Flex from '@talkdesk/cobalt-react-flex'
import { Text } from '@talkdesk/cobalt-react-typography'
import Message from '@talkdesk/cobalt-react-message'
import Button from '@talkdesk/cobalt-react-button'
import Box from '@talkdesk/cobalt-react-box'
import { Icon } from '@talkdesk/cobalt-react-icon'
import { Portal } from '@talkdesk/cobalt-react-portal-provider'
import Modal, { Dialog } from '@talkdesk/cobalt-react-modal'

const MFASettings = ({
  onSetupMFA,
  onSetupMFAMechanism,
  onRecoveryCodesRenew,
  onDismissMFAMechanismConfirm,
  onSelectMFAToRevoke,
  enabledMFAMechanisms,
  mfaEnforced,
  hasMFAEnabled,
  isLocalLoginEnabled,
  isMFASetupInProgress,
  recoveryOTPRegisterErrorCode,
  mfaToRevoke = RECOVERY_OTP,
  isRecoveryCodesRenewInProgress = false,
  recoveryCodeList,
  goToDisableMFAPage,
}) => {
  const [toastError, setToastError] = useState(null)
  const [showMFAEnforcedWarning, setShowMFAEnforcedWarning] = useState(false)
  const [
    showRecoveryCodesRenewConfirmation,
    setShowRecoveryCodesRenewConfirmation,
  ] = useState(false)
  const [
    localRecoveryOTPRegisterErrorCode,
    setLocalRecoveryOTPRegisterErrorCode,
  ] = useState(null)
  const [localRecoveryCodeList, setLocalRecoveryCodeList] = useState(null)

  const previousErrorCodeRef = useRef(recoveryOTPRegisterErrorCode)
  const previousRecoveryCodeListRef = useRef(recoveryCodeList)
  // Capture latest showRecoveryCodesRenewConfirmation for use inside the effect
  // without retriggering it.
  const showRecoveryRef = useRef(showRecoveryCodesRenewConfirmation)
  // eslint-disable-next-line react-hooks/refs
  showRecoveryRef.current = showRecoveryCodesRenewConfirmation

  const dismissToastError = () => setToastError(null)

  useEffect(() => {
    const previousError = previousErrorCodeRef.current
    const previousList = previousRecoveryCodeListRef.current

    if (
      recoveryOTPRegisterErrorCode &&
      !previousError &&
      !showRecoveryRef.current
    ) {
      setToastError(
        <ToastError
          title={mapError(
            recoveryOTPRegisterErrorCode,
            RECOVERY_OTP_CONFIG_ERROR,
          )}
          onDismiss={dismissToastError}
        />,
      )
    }

    if (recoveryOTPRegisterErrorCode !== previousError) {
      setLocalRecoveryOTPRegisterErrorCode(recoveryOTPRegisterErrorCode)
    }

    if (recoveryCodeList !== previousList) {
      setLocalRecoveryCodeList(recoveryCodeList)
    }

    previousErrorCodeRef.current = recoveryOTPRegisterErrorCode
    previousRecoveryCodeListRef.current = recoveryCodeList
  }, [recoveryOTPRegisterErrorCode, recoveryCodeList])

  const isLastMfaMechanism = () =>
    enabledMFAMechanisms.filter((mfa) => mfa !== RECOVERY_OTP).length === 1

  const handleRecoveryCodesRenewConfirmDismiss = () => {
    setShowRecoveryCodesRenewConfirmation(false)
    setLocalRecoveryOTPRegisterErrorCode(null)
    setLocalRecoveryCodeList(null)
  }

  const handleRevokeAllMFA = () => {
    onSelectMFAToRevoke(RECOVERY_OTP)
    goToDisableMFAPage(RECOVERY_OTP)
  }

  const handleRevokeMFA = (mfaMechanism) => {
    onSelectMFAToRevoke(mfaMechanism)
    if (isLastMfaMechanism() && mfaEnforced) {
      setShowMFAEnforcedWarning(true)
    } else {
      goToDisableMFAPage(mfaMechanism)
    }
  }

  const handleRevokeMFADismiss = () => {
    setShowMFAEnforcedWarning(false)
    onDismissMFAMechanismConfirm()
  }

  const renderActionButton = () => {
    if (!hasMFAEnabled) {
      return (
        <Button
          htmlType='button'
          variation='outline'
          size='small'
          onClick={onSetupMFA}
          disabled={!isLocalLoginEnabled || isMFASetupInProgress}
        >
          {MFA_SETTINGS_ENABLE}
        </Button>
      )
    }

    return (
      <Button
        htmlType='button'
        type='danger'
        variation='outline'
        size='small'
        disabled={mfaEnforced || isMFASetupInProgress}
        onClick={handleRevokeAllMFA}
      >
        {MFA_SETTINGS_DISABLE}
      </Button>
    )
  }

  const renderMFAEnforcedWarning = () => {
    const title = Properties[mfaToRevoke].name
    return (
      <Portal>
        <Modal size='small' visible>
          <Flex alignX='end' paddingBottom='2'>
            <Icon
              name='close'
              size='small'
              color='white'
              onClick={handleRevokeMFADismiss}
            />
          </Flex>
          <Dialog>
            <Flex scrollable padding={['3', '5', '6']}>
              <WarningMessage
                title={title}
                message={REVOKE_MFA_MECHANISM_ENFORCED_MESSAGE}
                onClick={handleRevokeMFADismiss}
              />
            </Flex>
          </Dialog>
        </Modal>
      </Portal>
    )
  }

  const renderRecoveryCodesRenewConfirmation = () => {
    const errorCode = localRecoveryOTPRegisterErrorCode
    return (
      <RecoveryCodesRenewConfirm
        onDismiss={handleRecoveryCodesRenewConfirmDismiss}
        onConfirm={onRecoveryCodesRenew}
        isRenewInProgress={isRecoveryCodesRenewInProgress}
        otpList={localRecoveryCodeList}
        errorMessage={
          errorCode && mapError(errorCode, RECOVERY_OTP_CONFIG_ERROR)
        }
      />
    )
  }

  const renderMechanisms = () => (
    <Flex direction='column' gap='3'>
      <MechanismsList
        enabledMFAMechanisms={enabledMFAMechanisms}
        onSetup={onSetupMFAMechanism}
        onRevoke={handleRevokeMFA}
      />
      <RecoveryCodesOption
        onRenew={() => setShowRecoveryCodesRenewConfirmation(true)}
      />
    </Flex>
  )

  return (
    <Box paddingY='2'>
      <SettingItem
        title={MFA_SETTINGS_TITLE}
        actionButton={renderActionButton()}
      >
        {showMFAEnforcedWarning && renderMFAEnforcedWarning()}
        {showRecoveryCodesRenewConfirmation &&
          renderRecoveryCodesRenewConfirmation()}
        {hasMFAEnabled && renderMechanisms()}
        {!isLocalLoginEnabled && (
          <Message type='warning'>
            <Flex padding='3' gap='3' alignY='center'>
              <Text>{MFA_SETTINGS_LOCAL_LOGIN_DISABLED}</Text>
            </Flex>
          </Message>
        )}
      </SettingItem>
      {toastError}
    </Box>
  )
}

export default MFASettings
